import * as React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { useLocalizer } from './hooks/useLocalizer';

interface AIAutoAcceptCheckboxProps {
    requestId?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    localizer?: IStringLocalizer;
    registerPopoversCb?: (popoverIds: string[]) => void;
}

interface AIAutoAcceptAccountCapabilities {
    autoAcceptSurveyEnabled?: boolean;
    obtainmentServicesEnabled?: boolean;
}

export const getAIAutoAcceptCheckboxState = (
    capabilities?: AIAutoAcceptAccountCapabilities | null
) => {
    const accountAutoAcceptEnabled = capabilities?.autoAcceptSurveyEnabled === true;

    return {
        defaultChecked: accountAutoAcceptEnabled,
        disabledByAccountCapabilities: accountAutoAcceptEnabled
            && capabilities?.obtainmentServicesEnabled === true
    };
};

/**
 * Checkbox component for controlling whether an offline AI response should be auto-accepted.
 */
export const AIAutoAcceptCheckbox: React.FC<AIAutoAcceptCheckboxProps> = ({
    requestId,
    checked,
    onChange,
    disabled = false,
    localizer,
    registerPopoversCb
}) => {
    const localize = useLocalizer(localizer);
    const userCapabilities = useSelector((state: ApplicationState) =>
        state.appServices?.userContext?.userCapabilities
    );
    const {
        defaultChecked,
        disabledByAccountCapabilities
    } = getAIAutoAcceptCheckboxState(userCapabilities);
    const effectiveChecked = disabledByAccountCapabilities ? false : checked;
    const effectiveDisabled = disabled || disabledByAccountCapabilities;
    const helpText = disabledByAccountCapabilities
        ? "Auto-accept is not available because obtainment services are enabled for this account."
        : "When checked, the survey response will be accepted automatically after AI processing completes.";

    React.useEffect(() => {
        onChange(disabledByAccountCapabilities ? false : defaultChecked);
    }, [requestId, defaultChecked, disabledByAccountCapabilities, onChange]);

    React.useEffect(() => {
        if (registerPopoversCb) {
            setTimeout(() => {
                registerPopoversCb(['ai-auto-accept-popover']);
            }, 100);
        }
    }, [registerPopoversCb]);

    return (
        <div className="form-check" style={{ marginBottom: '15px' }}>
            <input
                type="checkbox"
                className="form-check-input"
                id="autoAcceptSurveyResponse"
                checked={effectiveChecked}
                onChange={(e) => onChange(e.target.checked)}
                disabled={effectiveDisabled}
            />
            <label
                className="form-check-label"
                htmlFor="autoAcceptSurveyResponse"
                style={{ marginLeft: '7px', fontWeight: 'normal' }}
            >
                {localize("Auto-accept survey after AI processing")}
            </label>
            <a
                id="ai-auto-accept-popover"
                data-container="body"
                role="button"
                className="m-l-5"
                data-toggle="popover"
                data-html="true"
                data-placement="top"
                data-content={localize(helpText)}
            >
                <img height="14" src="/images/help-icon-dark-gray.png" />
            </a>
        </div>
    );
};

export default AIAutoAcceptCheckbox;
